import { Modal, ModalClose, ModalDialog } from '@mui/joy';

import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Header from '@/components/CodeHeader';
import { UserJourney } from '@/components/UserJourney';
import {
  useChangePaymentPlanMutation,
  useCurrentOrganization,
  useUpdateOrganization,
} from '@/hooks/useSwr';
import { ChangePaymentPlanTierSchema } from '@/types/schema';

import { useBillingPlanStore } from '../PlansAndBillingV2/store';
import { useHandleSubscribeModalOpen } from '../PlansAndBillingV2/store/helpers';
import { isTierNeedingCard } from '../PlansAndBillingV2/utils/isTierNeedingCard';
import { SubscribeToPlanStep } from '../utils/constants';
import { AddPaymentInfoModalCard } from './AddPaymentInfoModalCard';
import { ClaimCreditsModalCard } from './ClaimCreditsModalCard';
import { ManageSpendModalCard } from './ManageSpendModalCard';

export const SubscribeToPlanModal = ({
  setError,
  targetPlanTier,
}: {
  setError: (error: string | null) => void;
  targetPlanTier: ChangePaymentPlanTierSchema | null;
}) => {
  const clientSecret = useBillingPlanStore((s) => s.stripeClientSecret);
  const showManageSpend = useBillingPlanStore((s) => s.showMangeSpend);
  const showAddCard = useBillingPlanStore((s) => s.showAddCard);
  const showClaimGift = useBillingPlanStore((s) => s.showClaimGift);

  const isOpen = useBillingPlanStore((s) => s.isSubscribeModalOpen);
  const handleSubscribeOpen = useHandleSubscribeModalOpen();

  const { data: organization } = useCurrentOrganization();
  const [displayName, setDisplayName] = useState<string>(
    organization?.display_name ?? ''
  );
  const {
    trigger: updateOrganizationInfo,
    isMutating: isUpdatingOrganizationInfo,
  } = useUpdateOrganization();
  const isUpgradeFromPersonal =
    targetPlanTier !== 'developer' && Boolean(organization?.is_personal);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const billingStep = searchParams.get('billing_step');

  const steps: SubscribeToPlanStep[] = [];
  const isGiftRequiredRedirection = billingStep === 'claim_gift';
  if (showManageSpend || isGiftRequiredRedirection) {
    steps.push('manage_spend');
  }
  if (showAddCard || isGiftRequiredRedirection) {
    steps.push('add_card');
  }
  if (showClaimGift || isGiftRequiredRedirection) {
    steps.push('claim_gift');
  }

  const setIsOpen = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setCurrentIndex(0);
      }
      handleSubscribeOpen(isOpen);
    },
    [handleSubscribeOpen]
  );
  const setIsWelcomeMessageModalOpen = useBillingPlanStore(
    (s) => s.setIsWelcomeMessageModalOpen
  );

  const { trigger: changePlanTrigger, isMutating: isChangePlanMutating } =
    useChangePaymentPlanMutation();

  const handleEndOfFlow = useCallback(async () => {
    if (!targetPlanTier) return;
    try {
      await changePlanTrigger({
        json: {
          tier: targetPlanTier,
        },
      });

      if (isTierNeedingCard(targetPlanTier)) {
        setIsWelcomeMessageModalOpen(true);
      }
    } catch (e) {
      if (e instanceof Error && e.message) {
        setError(e.message);
      } else {
        setError('Something went wrong.');
      }
    }
  }, [
    targetPlanTier,
    changePlanTrigger,
    setError,
    setIsWelcomeMessageModalOpen,
  ]);

  const incrementCurrentIndex = useCallback(async () => {
    if (currentIndex < steps.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      await handleEndOfFlow();
      if (isUpgradeFromPersonal) {
        // Don't block this modal from closing.
        // Not super important this succeeds
        // if we allow editing org name in ui.
        updateOrganizationInfo({
          json: {
            display_name: displayName,
          },
        });
      }
      setIsOpen(false);
    }
  }, [
    steps.length,
    currentIndex,
    setCurrentIndex,
    setIsOpen,
    updateOrganizationInfo,
    isUpgradeFromPersonal,
    displayName,
    handleEndOfFlow,
  ]);

  const decrementCurrentIndex = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const manageSpendCard = (
    <ManageSpendModalCard
      onNext={incrementCurrentIndex}
      targetPlanTier={targetPlanTier}
      isLoading={
        isChangePlanMutating ||
        (isUpgradeFromPersonal && isUpdatingOrganizationInfo)
      }
      displayName={displayName}
      setDisplayName={setDisplayName}
    />
  );

  const addPaymentInfoCard = (
    <AddPaymentInfoModalCard
      clientSecret={clientSecret}
      onPrev={
        steps.indexOf('add_card') !== 0 ? decrementCurrentIndex : undefined
      }
      onNext={
        steps.indexOf('add_card') !== -1 ? incrementCurrentIndex : undefined
      }
      isChangingPlan={
        isChangePlanMutating ||
        (isUpgradeFromPersonal && isUpdatingOrganizationInfo)
      }
      targetPlanTier={targetPlanTier}
    />
  );

  const claimGiftCard = (
    <ClaimCreditsModalCard
      onNext={incrementCurrentIndex}
      isMutating={isChangePlanMutating}
    />
  );

  useEffect(() => {
    if (billingStep === 'subscribe_success') {
      handleEndOfFlow();
      setIsOpen(false);
      setSearchParams((prev) => {
        prev.delete('billing_step');
        prev.append('tab', '1');
        return prev;
      });
    } else if (billingStep === 'subscribe_error') {
      setError(searchParams.get('error_message'));
      setSearchParams((prev) => {
        prev.delete('billing_step');
        prev.delete('error_message');
        return prev;
      });
    } else if (billingStep === 'claim_gift') {
      setIsOpen(true);
      setCurrentIndex(steps.indexOf('claim_gift'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planCards: Record<SubscribeToPlanStep, ReactElement> = {
    manage_spend: manageSpendCard,
    add_card: addPaymentInfoCard,
    claim_gift: claimGiftCard,
  };
  const planTitles: Record<SubscribeToPlanStep, string> = {
    manage_spend: 'Setup team',
    add_card: 'Add card',
    claim_gift: 'Claim gift',
  };

  return (
    <Modal
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        setIsOpen(false);
      }}
    >
      <ModalDialog variant="outlined" sx={{ width: 700, overflow: 'auto' }}>
        <div>
          <div className="flex flex-col items-stretch gap-6 p-4">
            <div className="flex flex-col gap-2">
              {steps.length > 1 ? (
                <>
                  <Header text={'Set up billing'} />
                  <UserJourney
                    steps={steps.map((step) => planTitles[step])}
                    currentIndex={currentIndex}
                  />
                </>
              ) : steps.length === 1 && steps[0] === 'add_card' ? (
                <Header text="Update Payment Method" />
              ) : (
                isUpgradeFromPersonal && 'Upgrade to a team plan'
              )}
            </div>
            {planCards[steps[currentIndex]]}
          </div>
          <ModalClose />
        </div>
      </ModalDialog>
    </Modal>
  );
};
